import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { supabase } from '../../../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { 
  FolderTree, 
  Search, 
  Plus, 
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowUpDown,
  Save,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';

interface Category {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description: string | null;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
  course_count?: number;
  created_at: string;
  updated_at?: string;
}

const AdminCategoriesPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesPerPage] = useState(10);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: ''
  });

  useEffect(() => {
    if (tenant?.id) {
      fetchCategories();
    }
  }, [tenant?.id, sortBy, sortOrder]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      // Utiliser le filtrage par domaine
      let query = supabase
        .from('elearn_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      
      // Appliquer le filtre domaine
      query = applyDomainFilterToQuery(query);

      // Appliquer le filtre de recherche si présent
      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`);
      }

      const { data: categoriesData, error } = await query;

      if (error) throw error;

      // Récupérer le nombre de cours par catégorie
      const categoriesWithCounts = await Promise.all(
        (categoriesData || []).map(async (category) => {
          // Compter les cours associés à cette catégorie avec filtrage domaine
          let countQuery = supabase
            .from('elearn_courses')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', category.id);
          
          // Appliquer le filtre domaine
          countQuery = applyDomainFilterToQuery(countQuery);
          
          const { count, error: countError } = await countQuery;

          if (countError) {
            console.error('Erreur lors du comptage des cours:', countError);
            return { ...category, course_count: 0 };
          }

          return { ...category, course_count: count || 0 };
        })
      );

      setCategories(categoriesWithCounts);
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les catégories',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from name
    if (name === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || ''
    });
    setIsDialogOpen(true);
  };

  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSaveCategory();
  };

  const handleSaveCategory = async () => {
    // Validation des champs requis
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast({
        title: 'Erreur',
        description: 'Le nom et le slug sont requis',
        variant: 'destructive',
      });
      return;
    }
    
    // Définir l'état de chargement
    setLoading(true);
    
    try {
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();
      
      if (editingCategory) {
        // Mise à jour d'une catégorie existante
        let updateQuery = supabase
          .from('elearn_categories')
          .update({
            name: formData.name,
            slug: formData.slug,
            description: formData.description || null,
            updated_at: new Date().toISOString(),
            domain: domain || undefined
          })
          .eq('id', editingCategory.id);
        
        // Appliquer le filtre domaine
        updateQuery = applyDomainFilterToQuery(updateQuery);
        
        const { error } = await updateQuery;
        
        if (error) throw error;
        
        // Actualiser les données
        await fetchCategories();
        
        toast({
          title: 'Succès',
          description: 'Catégorie mise à jour avec succès',
          variant: 'default',
        });
      } else {
        // Création d'une nouvelle catégorie
        const newCategoryId = uuidv4();
        const { error } = await supabase
          .from('elearn_categories')
          .insert([
            {
              id: newCategoryId,
              tenant_id: parseInt(tenantId),
              domain: domain || undefined,
              name: formData.name,
              slug: formData.slug,
              description: formData.description || null,
              icon: 'FolderTree',
              color: '#3B82F6',
              sort_order: 0,
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          ]);
        
        if (error) throw error;
        
        // Actualiser les données
        await fetchCategories();
        
        toast({
          title: 'Succès',
          description: 'Catégorie créée avec succès',
          variant: 'default',
        });
      }
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la catégorie:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la catégorie',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (category: Category) => {
    if (category.course_count && category.course_count > 0) {
      toast({
        title: 'Action impossible',
        description: `La catégorie "${category.name}" contient des cours. Veuillez d'abord supprimer ou déplacer ces cours.`,
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setDeletingCategoryId(category.id);
      
      let deleteQuery = supabase
        .from('elearn_categories')
        .delete()
        .eq('id', category.id);
      
      // Appliquer le filtre domaine
      deleteQuery = applyDomainFilterToQuery(deleteQuery);
      
      const { error } = await deleteQuery;
      
      if (error) throw error;
      
      // Actualiser les données
      await fetchCategories();
      
      toast({
        title: 'Succès',
        description: `La catégorie "${category.name}" a été supprimée`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la catégorie',
        variant: 'destructive',
      });
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Réinitialiser la page courante lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);
  
  // Calculer les indices des catégories à afficher sur la page courante
  const indexOfLastCategory = currentPage * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstCategory, indexOfLastCategory);
  
  // Fonction pour changer de page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredCategories.length / categoriesPerPage);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FolderTree className="h-8 w-8 text-[#D4AF37]" />
              Catégories de Cours
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredCategories.length} catégorie{filteredCategories.length > 1 ? 's' : ''} trouvée{filteredCategories.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button 
            onClick={handleAddCategory}
            className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90"
          >
            <Plus className="mr-2" size={18} />
            Nouvelle catégorie
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{categories.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Cours catégorisés</div>
            <div className="text-2xl font-bold text-blue-400">
              {categories.reduce((sum, cat) => sum + (cat.course_count || 0), 0)}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Moyenne par catégorie</div>
            <div className="text-2xl font-bold text-[#D4AF37]">
              {categories.length > 0 
                ? Math.round(categories.reduce((sum, cat) => sum + (cat.course_count || 0), 0) / categories.length * 10) / 10
                : 0}
            </div>
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
            />
          </div>
        </div>

        {/* Table des catégories */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <FolderTree className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucune catégorie trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Nom
                        <ArrowUpDown size={14} className={sortBy === 'name' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('course_count')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Cours
                        <ArrowUpDown size={14} className={sortBy === 'course_count' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Date création
                        <ArrowUpDown size={14} className={sortBy === 'created_at' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentCategories.map((category, index) => (
                    <tr
                      key={category.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                            <FolderTree className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">{category.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-400">{category.slug}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{category.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">{category.course_count || 0}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{formatDate(category.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 text-white">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-gray-900 text-white border-white/10">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer hover:bg-white/10"
                              onClick={() => handleEditCategory(category)}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              <span>Modifier</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem 
                              className="cursor-pointer text-red-500 hover:bg-white/10 hover:text-red-500"
                              onClick={() => handleDeleteCategory(category)} 
                              disabled={deletingCategoryId === category.id || Boolean(category.course_count && category.course_count > 0)}
                            >
                              {deletingCategoryId === category.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Suppression...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Supprimer
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Contrôles de pagination */}
          {filteredCategories.length > 0 && (
              <div className="flex justify-between items-center p-4 border-t border-white/10">
                <div className="text-sm text-white/70">
                  Catégories ({filteredCategories.length}) - Page {currentPage}/{totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Afficher les pages autour de la page courante
                    let pageToShow;
                    if (totalPages <= 5) {
                      // Si moins de 5 pages, afficher toutes les pages
                      pageToShow = i + 1;
                    } else if (currentPage <= 3) {
                      // Si on est au début, afficher les 5 premières pages
                      pageToShow = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Si on est à la fin, afficher les 5 dernières pages
                      pageToShow = totalPages - 4 + i;
                    } else {
                      // Sinon, afficher 2 pages avant et 2 pages après la page courante
                      pageToShow = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageToShow}
                        variant={currentPage === pageToShow ? "default" : "outline"}
                        size="sm"
                        onClick={() => paginate(pageToShow)}
                        className={currentPage === pageToShow 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "border-white/10 text-white hover:bg-white/5"}
                      >
                        {pageToShow}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
          )}
        </div>
      </div>

      {/* Dialog pour ajouter/modifier une catégorie */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-gray-900 text-white border-white/10">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
            <DialogDescription>
              {editingCategory 
                ? 'Modifiez les informations de la catégorie ci-dessous.' 
                : 'Remplissez les informations pour créer une nouvelle catégorie.'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nom de la catégorie"
                className="bg-black/20 border-white/10 text-white"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                placeholder="slug-de-la-categorie"
                className="bg-black/20 border-white/10 text-white"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description de la catégorie"
                className="bg-black/20 border-white/10 text-white"
                rows={3}
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="border-white/10 text-white hover:bg-white/10"
              >
                <X className="mr-2" size={16} />
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    {editingCategory ? 'Mise à jour...' : 'Création...'}
                  </>
                ) : (
                  <>
                    <Save className="mr-2" size={16} />
                    {editingCategory ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCategoriesPage;
